/** Reusable motion primitives: masked line-by-line reveal + scroll fade-in. */
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

/** Split a string into words wrapped in overflow-hidden spans, animating y 100% → 0. */
export const RevealText = ({ text, as = "h1", className = "", delay = 0, stagger = 0.06 }) => {
  const Tag = motion[as] || motion.h1;
  const words = String(text).split(" ");
  return (
    <Tag className={className} aria-label={text}>
      {words.map((w, i) => (
        <span key={i} className="line-wrap mr-[0.28em] align-baseline inline-block">
          <motion.span
            className="line-inner inline-block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: delay + i * stagger }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

/** Scroll-triggered upward fade for arbitrary children. */
export const Reveal = ({ children, y = 24, delay = 0, once = true, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, amount: 0.2 });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
};

/** Masked reveal of each line of text (accepts array of lines). */
export const RevealLines = ({ lines, as = "h1", className = "", delay = 0 }) => {
  const Tag = motion[as] || motion.h1;
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <span key={i} className="line-wrap">
          <motion.span
            className="line-inner"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: delay + i * 0.15 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};
